import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaHome, FaUser, FaSignOutAlt, FaBell } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import RegistrationModal from './RegistrationModal';
import API_URL, { getImageUrl } from '../utils/api';
import { Link } from 'react-router-dom';
const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [bounce, setBounce] = useState(false);
  const [hasVisibleGallery, setHasVisibleGallery] = useState(false);
  const [propertiesDropdownOpen, setPropertiesDropdownOpen] = useState(false);
  const [mobilePropertiesOpen, setMobilePropertiesOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsLoading, setNotificationsLoading] = useState(true);
  const [logoUrl, setLogoUrl] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, token } = useAuth();

  // Debug: Log user state
  useEffect(() => {
    console.log('Navbar - User state:', user);
  }, [user]);

  // Fetch dynamic brand logo
  useEffect(() => {
    fetch(`${API_URL}/api/settings/brand.logo`)
      .then(res => res.ok ? res.json() : null)
      .then(data => { if (data && data.value) setLogoUrl(data.value); })
      .catch(() => {});
  }, []);

  const isActive = (path) => location.pathname === path;

  const handleChoicePropertiesClick = (e) => {
    e.preventDefault();
    setPropertiesDropdownOpen(false);
    
    if (location.pathname === '/') {
      // Already on home page — just scroll directly
      const element = document.getElementById('choice-properties');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      // Coming from another page — write the scroll target that Home.js watches,
      // then navigate. Home's pageshow / mount effect will pick it up.
      try {
        sessionStorage.setItem('restoreScrollId', 'section-choice-properties');
        sessionStorage.setItem('restoreScrollSection', 'choice-properties');
      } catch (_) {}
      navigate('/');
    }
  };

  const fetchNotifications = async () => {
    setNotificationsLoading(true);
    try {
      // Use token from auth context if available, otherwise try localStorage
      const authToken = token || localStorage.getItem('token');
      
      // Authenticated users get /me (includes public + targeted-to-their-phone)
      // Guests get /public (public audience only)
      const url = authToken ? `${API_URL}/api/notifications/me` : `${API_URL}/api/notifications/public`;
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
      const res = await fetch(url, { headers });
      if (!res.ok) {
        setNotifications([]);
        setUnreadCount(0);
        return;
      }
      const data = await res.json();
      const items = Array.isArray(data) ? data : [];

      // For guest users, mark notifications as read if they're in localStorage
      if (!authToken) {
        const readNotifications = JSON.parse(localStorage.getItem('readNotifications') || '[]');
        items.forEach((item) => {
          if (readNotifications.includes(item._id)) {
            item.isRead = true;
          }
        });
      }

      setNotifications(items);
      setUnreadCount(items.filter((item) => !item.isRead).length);
    } catch (error) {
      console.error('Failed to load notifications:', error);
      setNotifications([]);
      setUnreadCount(0);
    } finally {
      setNotificationsLoading(false);
    }
  };

  const markNotificationAsRead = async (notificationId) => {
    console.log('📌 Marking notification as read:', notificationId);

    if (token) {
      // Authenticated user - send to backend
      try {
        const response = await fetch(`${API_URL}/api/notifications/${notificationId}/read`, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}` }
        });

        console.log('✅ Backend response status:', response.status);

        if (!response.ok) {
          console.error('❌ Failed to mark notification as read:', response.status);
          fetchNotifications();
          return;
        }

        const data = await response.json();
        console.log('✅ Backend response:', data);
      } catch (error) {
        console.error('❌ Failed to mark notification as read:', error);
        fetchNotifications();
        return;
      }
    } else {
      // Guest user - store in localStorage
      console.log('👤 Guest user - storing read notification in localStorage');
      const readNotifications = JSON.parse(localStorage.getItem('readNotifications') || '[]');
      if (!readNotifications.includes(notificationId)) {
        readNotifications.push(notificationId);
        localStorage.setItem('readNotifications', JSON.stringify(readNotifications));
      }
    }

    // Update UI immediately for both authenticated and guest users
    setNotifications((prev) => {
      const updated = prev.map((n) =>
        n._id === notificationId ? { ...n, isRead: true } : n
      );
      console.log('✅ Updated notifications:', updated);
      return updated;
    });

    setUnreadCount((prev) => {
      const newCount = Math.max(0, prev - 1);
      console.log('✅ Unread count updated to:', newCount);
      return newCount;
    });
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    // Check if gallery section is enabled
    fetch(`${API_URL}/api/settings/gallery.enabled`)
      .then(res => {
        if (!res.ok) {
          // If settings API not available, default to true
          setHasVisibleGallery(true);
          return null;
        }
        return res.json();
      })
      .then(data => {
        if (data) setHasVisibleGallery(data.value !== false);
      })
      .catch(() => setHasVisibleGallery(true));
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isOpen && !e.target.closest('nav')) {
        setIsOpen(false);
      }
      if (propertiesDropdownOpen && !e.target.closest('.properties-dropdown-container')) {
        setPropertiesDropdownOpen(false);
      }
      if (userMenuOpen && !e.target.closest('.user-menu-container')) {
        setUserMenuOpen(false);
      }
      if (notificationOpen && !e.target.closest('.notification-dropdown-container')) {
        setNotificationOpen(false);
      }
    };
    const handleScroll = () => {
      if (isOpen) {
        setIsOpen(false);
      }
      if (propertiesDropdownOpen) {
        setPropertiesDropdownOpen(false);
      }
      if (userMenuOpen) {
        setUserMenuOpen(false);
      }
      if (notificationOpen) {
        setNotificationOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    window.addEventListener('scroll', handleScroll);
    return () => {
      document.removeEventListener('click', handleClickOutside);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isOpen, propertiesDropdownOpen, userMenuOpen, notificationOpen]);

  const handleLinkClick = () => {
    setIsOpen(false);
  };

  const handleHomeNavigation = (e) => {
    e?.preventDefault();

    try {
      const currentPath = location.pathname;
      const currentSearch = location.search || '';
      const pathSegments = currentPath.split('/').filter(Boolean);

      if (pathSegments[0] === 'category' && pathSegments[1]) {
        sessionStorage.setItem('restoreScrollId', `cat-${pathSegments[1]}`);
        sessionStorage.setItem('restoreScrollSection', 'categories');
        sessionStorage.setItem('homeRestoreId', `cat-${pathSegments[1]}`);
        sessionStorage.setItem('homeRestoreSection', 'categories');
        sessionStorage.setItem('pendingListingRoute', `${currentPath}${currentSearch}`);
      } else if (pathSegments[0] === 'choice-category' && pathSegments[1]) {
        sessionStorage.setItem('restoreScrollId', `choice-${pathSegments[1]}`);
        sessionStorage.setItem('restoreScrollSection', 'choice');
        sessionStorage.setItem('homeRestoreId', `choice-${pathSegments[1]}`);
        sessionStorage.setItem('homeRestoreSection', 'choice');
        sessionStorage.setItem('pendingListingRoute', `${currentPath}${currentSearch}`);
      } else {
        const rawContext = sessionStorage.getItem('propertyDetailsContext') || sessionStorage.getItem('lastClickedProperty');
        if (rawContext) {
          const context = JSON.parse(rawContext);
          if (context?.isChoice && context?.fromCategory) {
            sessionStorage.setItem('restoreScrollId', `choice-${context.fromCategory}`);
            sessionStorage.setItem('restoreScrollSection', 'choice');
            sessionStorage.setItem('homeRestoreId', `choice-${context.fromCategory}`);
            sessionStorage.setItem('homeRestoreSection', 'choice');
          } else if (context?.isCategory && context?.fromCategory) {
            sessionStorage.setItem('restoreScrollId', `cat-${context.fromCategory}`);
            sessionStorage.setItem('restoreScrollSection', 'categories');
            sessionStorage.setItem('homeRestoreId', `cat-${context.fromCategory}`);
            sessionStorage.setItem('homeRestoreSection', 'categories');
          }
        }
      }
    } catch (_) {}

    setIsOpen(false);
    setPropertiesDropdownOpen(false);
    setMobilePropertiesOpen(false);
    navigate('/');
  };

  return (
    <>
    <nav className="bg-white/70 backdrop-blur-lg shadow-lg fixed w-full z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <button type="button" onClick={handleHomeNavigation} className="flex items-center focus:outline-none focus:ring-0 focus-visible:ring-0 rounded-sm">
            <img 
              src={logoUrl ? getImageUrl(logoUrl) : '/tanavi-updated-logo.png'} 
              alt="Tanavi Properties" 
              className="h-24 md:h-32 w-auto object-contain mt-2"
            />
          </button>
          
          <div className="hidden md:flex items-center space-x-7 relative">
            <button type="button" onClick={handleHomeNavigation} className={`px-4 py-2 font-medium transition ${isActive('/') ? 'bg-primary text-white rounded' : 'text-gray-700 hover:text-primary'}`}>Home</button>
            <button 
              onClick={() => setIsModalOpen(true)}
              className="px-4 py-2 text-gray-700 hover:text-primary font-medium transition"
            >
              List Your Property
            </button>
            {/* Properties Dropdown */}
            <div className="relative properties-dropdown-container">
              <button
                onClick={() => setPropertiesDropdownOpen(!propertiesDropdownOpen)}
                className={`px-4 py-2 font-medium transition flex items-center gap-1 ${
                  isActive('/category/all') || location.pathname.startsWith('/category/') || location.pathname.startsWith('/choice-category/')
                    ? 'bg-primary text-white rounded' 
                    : 'text-gray-700 hover:text-primary'
                }`}
              >
                Properties
                <svg 
                  className={`w-4 h-4 transition-transform ${propertiesDropdownOpen ? 'rotate-180' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {propertiesDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-100 py-2 z-50">
                  <Link 
                    to="/category/all" 
                    onClick={() => setPropertiesDropdownOpen(false)}
                    className="block px-4 py-2.5 text-gray-700 hover:bg-primary hover:text-white transition font-medium"
                  >
                    All Properties
                  </Link>
                  <Link 
                    to="/#choice-properties" 
                    onClick={handleChoicePropertiesClick}
                    className="block px-4 py-2.5 text-gray-700 hover:bg-primary hover:text-white transition font-medium"
                  >
                    Choice Properties
                  </Link>
                </div>
              )}
            </div>
            
            {hasVisibleGallery && (
              <Link to="/blogs" className={`px-4 py-2 font-medium transition ${isActive('/blogs') ? 'bg-primary text-white rounded' : 'text-gray-700 hover:text-primary'}`}>Gallery</Link>
            )}
            <Link to="/buy-sell" className={`px-4 py-2 font-medium transition ${isActive('/buy-sell') ? 'bg-primary text-white rounded' : 'text-gray-700 hover:text-primary'}`}>Buy & Sell</Link>
            <Link to="/guide" className={`px-4 py-2 font-medium transition ${isActive('/guide') ? 'bg-primary text-white rounded' : 'text-gray-700 hover:text-primary'}`}>Guide</Link>
            <Link to="/about" className={`px-4 py-2 font-medium transition ${isActive('/about') ? 'bg-primary text-white rounded' : 'text-gray-700 hover:text-primary'}`}>About</Link>

            <div className="relative notification-dropdown-container">
              <button
                onClick={() => {
                  // If closing, just close
                  if (notificationOpen) {
                    setNotificationOpen(false);
                    return;
                  }

                  // If opening, mark all as read and open dropdown in same click
                  // 1. For authenticated users, call backend API (fire and forget)
                  if (token) {
                    notifications.forEach((notif) => {
                      if (!notif.isRead) {
                        fetch(`${API_URL}/api/notifications/${notif._id}/read`, {
                          method: 'PUT',
                          headers: { Authorization: `Bearer ${token}` }
                        }).catch(err => console.error('Failed to mark as read:', err));
                      }
                    });
                  } else {
                    // 2. For guests, store in localStorage
                    const readNotifications = JSON.parse(localStorage.getItem('readNotifications') || '[]');
                    notifications.forEach((notif) => {
                      if (!notif.isRead && !readNotifications.includes(notif._id)) {
                        readNotifications.push(notif._id);
                      }
                    });
                    localStorage.setItem('readNotifications', JSON.stringify(readNotifications));
                  }
                  
                  // 3. Update UI state - count to 0 and open dropdown in same batch
                  setUnreadCount(0);
                  setNotificationOpen(true);
                }}
                className="relative px-2 sm:px-3 md:px-4 py-2 text-gray-900 hover:text-primary font-medium transition flex items-center gap-2 md:gap-2"
                aria-label="Notifications"
              >
                <FaBell className="text-base sm:text-lg md:text-lg" />
                {unreadCount > 0 && (
                  <span className="absolute top-0 right-0 sm:top-1 sm:right-1 md:top-1 md:right-2 inline-flex items-center justify-center px-1.5 sm:px-2 py-0.5 sm:py-1 text-[9px] sm:text-[10px] md:text-[11px] font-semibold leading-none text-white bg-red-600 rounded-full">
                    {unreadCount}
                  </span>
                )}
              </button>
              {notificationOpen && (
              <div className="absolute right-0 top-full mt-2 w-[calc(100vw-1rem)] sm:w-80 md:w-96 rounded-3xl bg-slate-950/95 border border-slate-700 shadow-2xl backdrop-blur-xl overflow-hidden z-50">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
                    <span className="text-sm font-semibold text-white">Notifications</span>
                    <button onClick={() => setNotificationOpen(false)} aria-label="Close notifications" className="text-slate-400 hover:text-white text-xl leading-none">×</button>
                  </div>
                  {notificationsLoading ? (
                    <div className="px-4 py-4 text-sm text-slate-300">Loading notifications...</div>
                  ) : notifications.length === 0 ? (
                    <div className="px-4 py-5 text-sm text-slate-300">No notifications at the moment.</div>
                  ) : (
                    <div className="space-y-2 px-3 py-3 max-h-[420px] overflow-y-auto">
                      {notifications.map(notification => (
                        <div
                          key={notification._id}
                          className={`w-full rounded-3xl p-4 text-left transition ${notification.isRead ? 'bg-slate-900/60 hover:bg-slate-900/80' : 'bg-slate-800/90 shadow-sm hover:bg-slate-800'}`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-white truncate">{notification.title}</p>
                              <p className="mt-1 text-sm text-slate-300 line-clamp-2">{notification.message}</p>
                            </div>
                            {!notification.isRead && (
                              <span className="rounded-full bg-amber-400 px-2 py-1 text-[11px] font-semibold text-slate-950">New</span>
                            )}
                          </div>
                          <p className="mt-3 text-[11px] text-slate-400">{new Date(notification.createdAt).toLocaleString()}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {user && (
              <div className="relative user-menu-container">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-primary font-medium transition"
                >
                  <FaUser className="text-lg" />
                  <span>{user.name}</span>
                </button>
                
                {userMenuOpen && (
                  <div className="absolute top-full right-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-100 py-2 z-50">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <p className="font-semibold text-gray-800">{user.name}</p>
                      <p className="text-sm text-gray-600">{user.email}</p>
                      <p className="text-sm text-gray-600">{user.phone}</p>
                    </div>
                    <Link
                      to="/profile"
                      onClick={() => setUserMenuOpen(false)}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-gray-700 hover:bg-gray-50 transition font-medium"
                    >
                      <FaUser />
                      <span>My Profile</span>
                    </Link>
                    <button
                      onClick={() => { logout(); setUserMenuOpen(false); }}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-red-600 hover:bg-red-50 transition font-medium"
                    >
                      <FaSignOutAlt />
                      <span>Logout</span>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Mobile: Hamburger Menu + Bell */}
          <div className="md:hidden flex items-center gap-1">
            {/* Hamburger Menu */}
            <button onClick={() => setIsOpen(!isOpen)} className="flex items-center justify-center w-10 h-10 relative">
              <div className="relative w-6 h-6">
                <span className={`absolute left-0 w-6 h-0.5 bg-gray-900 transition-all duration-300 ${isOpen ? 'top-3 rotate-45' : 'top-1'}`}></span>
                <span className={`absolute left-0 top-3 w-6 h-0.5 bg-gray-900 transition-all duration-300 ${isOpen ? 'opacity-0' : 'opacity-100'}`}></span>
                <span className={`absolute left-0 w-6 h-0.5 bg-gray-900 transition-all duration-300 ${isOpen ? 'top-3 -rotate-45' : 'top-5'}`}></span>
              </div>
            </button>

            {/* Bell Icon */}
            <div className="relative notification-dropdown-container">
              <button
                onClick={() => {
                  if (notificationOpen) {
                    setNotificationOpen(false);
                    return;
                  }
                  if (token) {
                    notifications.forEach((notif) => {
                      if (!notif.isRead) {
                        fetch(`${API_URL}/api/notifications/${notif._id}/read`, {
                          method: 'PUT',
                          headers: { Authorization: `Bearer ${token}` }
                        }).catch(err => console.error('Failed to mark as read:', err));
                      }
                    });
                  } else {
                    const readNotifications = JSON.parse(localStorage.getItem('readNotifications') || '[]');
                    notifications.forEach((notif) => {
                      if (!notif.isRead && !readNotifications.includes(notif._id)) {
                        readNotifications.push(notif._id);
                      }
                    });
                    localStorage.setItem('readNotifications', JSON.stringify(readNotifications));
                  }
                  setUnreadCount(0);
                  setNotificationOpen(true);
                }}
                className="flex items-center justify-center w-10 h-10 relative"
                aria-label="Notifications"
              >
                <FaBell className="text-lg text-gray-900" />
                {unreadCount > 0 && (
                  <span className="absolute top-0 right-0 inline-flex items-center justify-center w-5 h-5 text-[10px] font-semibold text-white bg-red-600 rounded-full">
                    {unreadCount}
                  </span>
                )}
              </button>

              {/* Dropdown - opens downward */}
              {notificationOpen && (
                <>
                  {/* Overlay */}
                  <div 
                    className="fixed inset-0 bg-black/30 z-40" 
                    onClick={() => setNotificationOpen(false)}
                  />
                  
                  {/* Notification Panel - Responsive Positioning */}
                  <div className="fixed top-16 left-3 right-3 sm:left-auto sm:right-4 sm:w-96 md:absolute md:top-full md:right-0 md:left-auto md:mt-2 md:w-80 z-50">
                    <div className="bg-slate-950/95 border border-slate-700 rounded-2xl shadow-2xl backdrop-blur-xl overflow-hidden max-h-96 flex flex-col">
                      {/* Header */}
                      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800 flex-shrink-0">
                        <h2 className="text-base font-semibold text-white">Notifications</h2>
                        <button 
                          onClick={() => setNotificationOpen(false)}
                          className="text-slate-400 hover:text-white text-2xl leading-none -mr-1"
                        >
                          ×
                        </button>
                      </div>

                      {/* Content */}
                      <div className="overflow-y-auto flex-1 min-h-0">
                        {notificationsLoading ? (
                          <div className="px-4 py-8 text-center text-slate-300 text-sm">Loading...</div>
                        ) : notifications.length === 0 ? (
                          <div className="px-4 py-10 text-center text-slate-300 text-sm">No notifications</div>
                        ) : (
                          <div className="space-y-2 p-3">
                            {notifications.map(notification => (
                              <div
                                key={notification._id}
                                className={`rounded-lg p-3 transition text-sm ${
                                  notification.isRead 
                                    ? 'bg-slate-800 hover:bg-slate-700' 
                                    : 'bg-slate-700 hover:bg-slate-600'
                                }`}
                              >
                                <div className="flex justify-between items-start gap-2">
                                  <div className="flex-1 min-w-0">
                                    <p className="font-semibold text-white truncate">{notification.title}</p>
                                    <p className="text-slate-300 text-xs mt-1 line-clamp-2">{notification.message}</p>
                                    <p className="text-slate-400 text-[11px] mt-1">
                                      {new Date(notification.createdAt).toLocaleString()}
                                    </p>
                                  </div>
                                  {!notification.isRead && (
                                    <div className="bg-amber-400 text-slate-950 text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0">New</div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {isOpen && (
        <div className="md:hidden bg-secondary/20 backdrop-blur-xl shadow-xl border-t border-gray-100">
          <div className="px-4 pt-3 pb-5 space-y-2">
            <Link to="/" onClick={handleLinkClick} className={`block px-4 py-3 rounded-xl transition font-semibold text-[15px] border ${isActive('/') ? 'bg-primary text-white border-primary shadow-md' : 'text-gray-700 bg-white border-gray-200 hover:bg-primary hover:text-white hover:border-primary shadow-sm'}`}>Home</Link>
            <button
              onClick={() => { setIsModalOpen(true); setIsOpen(false); }}
              className="w-full text-left px-4 py-3 text-gray-700 bg-white border border-gray-200 hover:bg-primary hover:text-white hover:border-primary rounded-xl font-semibold text-[15px] transition shadow-sm"
            >
              List Your Property
            </button>

            {/* Mobile Properties Dropdown */}
            <div>
              <button
                onClick={() => setMobilePropertiesOpen(!mobilePropertiesOpen)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition font-semibold text-[15px] border shadow-sm ${
                  isActive('/category/all') || location.pathname.startsWith('/category/') || location.pathname.startsWith('/choice-category/')
                    ? 'bg-primary text-white border-primary shadow-md'
                    : 'text-gray-700 bg-white border-gray-200 hover:bg-primary hover:text-white hover:border-primary'
                }`}
              >
                Properties
                <svg className={`w-4 h-4 transition-transform ${mobilePropertiesOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {mobilePropertiesOpen && (
                <div className="mt-2 ml-4 space-y-2">
                  <Link to="/category/all" onClick={handleLinkClick}
                    className="block px-4 py-2.5 rounded-xl text-gray-600 bg-white border border-gray-200 hover:bg-primary hover:text-white hover:border-primary transition font-medium text-[14px] shadow-sm">
                    All Properties
                  </Link>
                  <Link to="/#choice-properties" onClick={(e) => { handleChoicePropertiesClick(e); setIsOpen(false); setMobilePropertiesOpen(false); }}
                    className="block px-4 py-2.5 rounded-xl text-gray-600 bg-white border border-gray-200 hover:bg-primary hover:text-white hover:border-primary transition font-medium text-[14px] shadow-sm">
                    Choice Properties
                  </Link>
                </div>
              )}
            </div>

            {hasVisibleGallery && (
              <Link to="/blogs" onClick={handleLinkClick} className={`block px-4 py-3 rounded-xl transition font-semibold text-[15px] border shadow-sm ${isActive('/blogs') ? 'bg-primary text-white border-primary shadow-md' : 'text-gray-700 bg-white border-gray-200 hover:bg-primary hover:text-white hover:border-primary'}`}>Gallery</Link>
            )}
            <Link to="/buy-sell" onClick={handleLinkClick} className={`block px-4 py-3 rounded-xl transition font-semibold text-[15px] border shadow-sm ${isActive('/buy-sell') ? 'bg-primary text-white border-primary shadow-md' : 'text-gray-700 bg-white border-gray-200 hover:bg-primary hover:text-white hover:border-primary'}`}>Buy &amp; Sell</Link>
            <Link to="/guide" onClick={handleLinkClick} className={`block px-4 py-3 rounded-xl transition font-semibold text-[15px] border shadow-sm ${isActive('/guide') ? 'bg-primary text-white border-primary shadow-md' : 'text-gray-700 bg-white border-gray-200 hover:bg-primary hover:text-white hover:border-primary'}`}>Guide</Link>
            <Link to="/about" onClick={handleLinkClick} className={`block px-4 py-3 rounded-xl transition font-semibold text-[15px] border shadow-sm ${isActive('/about') ? 'bg-primary text-white border-primary shadow-md' : 'text-gray-700 bg-white border-gray-200 hover:bg-primary hover:text-white hover:border-primary'}`}>About</Link>

            {user && (
              <div className="border-t border-gray-200 pt-3 mt-3 space-y-2">
                <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl shadow-sm mb-1">
                  <p className="font-semibold text-gray-800">{user.name}</p>
                  <p className="text-sm text-gray-500">{user.email}</p>
                  <p className="text-sm text-gray-500">{user.phone}</p>
                </div>
                <Link to="/profile" onClick={handleLinkClick}
                  className="w-full flex items-center gap-2 px-4 py-3 text-gray-700 bg-white border border-gray-200 hover:bg-primary hover:text-white hover:border-primary rounded-xl font-semibold text-[15px] transition shadow-sm">
                  <FaUser />
                  <span>My Profile</span>
                </Link>
                <button onClick={() => { logout(); setIsOpen(false); }}
                  className="w-full flex items-center gap-2 px-4 py-3 text-red-600 bg-red-50 border border-red-200 hover:bg-red-600 hover:text-white hover:border-red-600 rounded-xl font-semibold text-[15px] transition shadow-sm">
                  <FaSignOutAlt />
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
    <RegistrationModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} modalType="list" />
    </>
  );
};

export default Navbar;
